import { useCallback, useEffect, useState } from 'react';
import type { DayView, SectionName } from '../lib/day';
import { dayApi, section } from '../lib/day';
import { ApiError } from '../lib/api';
import { openPrint } from '../lib/print';
import { formatDate, formatLitres, formatRs } from '../lib/money';
import DayReport from '../print/DayReport';
import { ReceiptSheets } from '../print/AdvanceReceipt';

/**
 * The Report tab — end of day.
 *
 * Shows where the three sections stand, the day's money both ways (cost and
 * cash out), the advances given, and the print buttons. Nothing prints without
 * going through the preview first.
 */

interface Reference {
  plants: { id: string; code: string; name: string }[];
}

interface Props {
  today: string;
  reference: Reference;
  userName: string;
}

export default function Report({ today, reference, userName }: Props) {
  const [date, setDate] = useState(today);
  const [view, setView] = useState<DayView | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setView(await dayApi.get(date));
      setError('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the day.');
    }
  }, [date]);

  useEffect(() => { void load(); }, [load]);

  if (!view) return <div className="empty">{error || 'Loading…'}</div>;

  const closed = view.day.closedAt !== null;
  const statuses = (['labour', 'diesel', 'misc'] as const).map((name) => ({
    name,
    status: section(view, name).status,
  }));
  const allSubmitted = statuses.every((row) => row.status !== 'open');
  const approvedCount = statuses.filter((row) => row.status === 'approved').length;

  // "Approved" on each receipt: whoever approved the day's labour.
  const labourRow = view.sections.find((row) => row.section === 'labour');
  const labourApproval = labourRow?.status === 'approved'
    ? { by: labourRow.approvedBy, name: labourRow.approvedByName, at: labourRow.approvedAt }
    : undefined;
  const advances = view.entries.flatMap((entry) =>
    entry.advances.map((advance) => ({ entry, advance, approval: labourApproval })),
  );

  function printDayReport() {
    openPrint(
      `${closed ? 'Approved' : 'Unapproved'} day report — ${formatDate(date)}`,
      <DayReport
        view={view!}
        plants={reference.plants}
        printedBy={userName}
        printedAt={new Date()}
      />,
    );
  }

  function printReceipts() {
    openPrint(
      `${advances.length} advance receipt${advances.length === 1 ? '' : 's'} — ${formatDate(date)}`,
      <ReceiptSheets items={advances} workDate={date} />,
    );
  }

  return (
    <>
      <DayNav date={date} today={today} onDate={setDate} />

      {error && <div className="banner b-no">{error}</div>}

      <div className="card">
        <h3>End of day — {formatDate(date)}</h3>
        {statuses.map((row) => (
          <div className="row" key={row.name}>
            <div className="name">{labelOf(row.name)}</div>
            {row.status === 'approved' ? (
              <span className="pill p-ok">Approved</span>
            ) : row.status === 'submitted' ? (
              <span className="pill p-wait">Submitted</span>
            ) : (
              <span className="pill p-no">Not submitted</span>
            )}
          </div>
        ))}
      </div>

      <div className="grid2">
        <div className="metric">
          <div className="k">Labour earned (paid weekly)</div>
          <div className="v">{formatRs(view.totals.labourRs)}</div>
          <div className="k">{view.totals.workersOnSite} worked</div>
        </div>
        <div className="metric">
          <div className="k">Diesel</div>
          <div className="v">{formatRs(view.totals.dieselRs)}</div>
          <div className="k">
            {formatLitres(view.totals.litresX100)} L @ {formatRs(view.day.dieselRateRs)}
          </div>
        </div>
        <div className="metric">
          <div className="k">Misc</div>
          <div className="v">{formatRs(view.totals.miscRs)}</div>
          <div className="k">{view.misc.length} items</div>
        </div>
        <div className="metric">
          <div className="k">Advances paid</div>
          <div className="v">{formatRs(view.totals.advancesRs)}</div>
          <div className="k">{advances.length} receipts</div>
        </div>
      </div>

      {/* The two numbers the owner actually reads: what the day cost, and what
          left the cash box. They are not the same, and the report says so. */}
      <div className="grid2" style={{ marginTop: 10 }}>
        <div className="metric" style={{ background: 'var(--bg-accent)' }}>
          <div className="k" style={{ color: 'var(--text-accent)' }}>Total cost for the day</div>
          <div className="v" style={{ color: 'var(--text-accent)' }}>
            {formatRs(view.totals.costRs)}
          </div>
          <div className="k" style={{ color: 'var(--text-accent)' }}>labour + diesel + misc</div>
        </div>
        <div className="metric" style={{ background: 'var(--bg-warning)' }}>
          <div className="k" style={{ color: 'var(--text-warning)' }}>Cash out today</div>
          <div className="v" style={{ color: 'var(--text-warning)' }}>
            {formatRs(view.totals.cashOutRs)}
          </div>
          <div className="k" style={{ color: 'var(--text-warning)' }}>
            diesel + misc + advances
          </div>
        </div>
      </div>

      {advances.length > 0 && (
        <div className="card">
          <h3>Advances paid today</h3>
          <div className="sub">
            Day cash, already deducted from the weekly pay. Receipts are printed from the
            finalise popup on the timesheet, or all together below.
          </div>
          {advances.map(({ entry, advance }) => (
            <div className="row" key={advance.id}>
              <div>
                <div className="name">{entry.workerName}</div>
                <div className="sub">
                  {advance.note && `${advance.note} · `}
                  {formatRs(entry.advancesThisWeekRs)} taken this pay week ·{' '}
                  {advance.signed ? (
                    <span className="pill p-ok">signed</span>
                  ) : (
                    <span className="pill p-no">not signed</span>
                  )}
                </div>
              </div>
              <span className="name">{formatRs(advance.amountRs)}</span>
            </div>
          ))}
          <div className="bar" style={{ marginTop: 12 }}>
            <button type="button" className="btn" onClick={printReceipts}>
              Print all {advances.length} receipt{advances.length === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      )}

      {closed ? (
        <div className="banner b-ok">
          Approved by the owner
          {view.day.closedAt && ` on ${new Date(view.day.closedAt).toLocaleString('en-GB')}`}. The
          approved report can be printed.
        </div>
      ) : allSubmitted ? (
        <div className="banner b-warn">
          All three sections submitted — waiting for the owner to approve ({approvedCount} of 3
          approved). An unapproved copy can be printed now.
        </div>
      ) : (
        <div className="banner b-info">
          Report unlocks when labour, diesel and misc are all submitted.
        </div>
      )}

      <div className="bar">
        <button
          type="button"
          className={closed ? 'btn pri' : 'btn'}
          disabled={!closed && !allSubmitted}
          onClick={printDayReport}
        >
          {closed ? 'Print approved report (A4)' : 'Print unapproved report (A4)'}
        </button>
      </div>
    </>
  );
}

function labelOf(name: SectionName): string {
  return name === 'labour' ? 'Labour' : name === 'diesel' ? 'Diesel' : 'Misc expenses';
}

function DayNav({
  date, today, onDate,
}: {
  date: string;
  today: string;
  onDate: (date: string) => void;
}) {
  const shift = (days: number) =>
    new Date(Date.parse(`${date}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);

  return (
    <div className="bar" style={{ margin: '10px 0 0', alignItems: 'center' }}>
      <button type="button" className="btn sm" style={{ minWidth: 0 }} onClick={() => onDate(shift(-1))}>
        ‹ Day before
      </button>
      <div style={{ flex: 2, textAlign: 'center', fontSize: 13, fontWeight: 500 }}>
        {formatDate(date)}
        {date === today && ' · today'}
      </div>
      <button
        type="button"
        className="btn sm"
        style={{ minWidth: 0 }}
        disabled={date >= today}
        onClick={() => onDate(shift(1))}
      >
        Day after ›
      </button>
      {date !== today && (
        <button type="button" className="btn sm" style={{ minWidth: 0 }} onClick={() => onDate(today)}>
          Back to today
        </button>
      )}
    </div>
  );
}
